import { type, view, Schema, MapSchema, ArraySchema } from "@colyseus/schema";
import { Player, CoopRole } from "./Player";
import { Location } from "./Location";
import { GameStep } from "./GameStep";
import { GameMode } from "./GameMode";

import { Expansion } from "./Expansion";
import { MapType } from "./MapType";
import { 
    getDefaultAdvancedSettings, 
    GameModeAdvancedSettings,
    FightModeSettings,
    HazardModeSettings,
    FlashModeSettings,
    DailyModeSettings,
    CoopModeSettings,
    CustomModeSettings,
    CustomBaseMode
} from "./GameModeAdvancedSettings";
import { GameModeStatus } from "./GameMode";
import { GameModeSettingsSchema } from "./GameModeSettingsSchema";
import { Team, TeamRanking, MAX_TEAMS } from "./Team";
import { generateRandomTeamName, TEAM_COLORS_CONFIG } from "@/lib/config/teams";

export class Coordinate extends Schema {
    @type("number") x: number = 0;
    @type("number") y: number = 0;
    @type("number") z: number = 0;
}

export class Photosphere extends Schema {
    @type("string") id: string = "";
    @type("string") weather: string = "";
    @type("string") time: string = "";
    @type("string") mapId: string = "";
    @type("string") mapName: string = "";
    @type("string") expansion: string = "";
    @type(Coordinate) coord = new Coordinate();
}

// Snapshot of a team's state at a specific round
export class TeamSnapshot extends Schema {
    @type("string") teamId: string = "";
    @type("string") name: string = "";
    @type("string") color: string = "";
    @type("int32") previousHp: number = 0; // HP before this round's damage was applied
    @type("int32") currentHp: number = 0; // HP after this round's damage
    @type("int32") maxHp: number = 0;
    @type("number") lastRoundDamage: number = 0;
    @type("boolean") isEliminated: boolean = false;
    @type("boolean") wasEliminatedThisRound: boolean = false; // True if team was eliminated during this round
}

export class RoundResult extends Schema {
    @type("int8") roundNumber: number = 0;
    @type(Photosphere) photosphere = new Photosphere();
    @type({ map: Player }) playerResults = new MapSchema<Player>();
    @type(["string"]) hazards = new ArraySchema<string>();
    @type([TeamSnapshot]) teamSnapshots = new ArraySchema<TeamSnapshot>(); // Fight mode: team states for this round
}

export class FinalRanking extends Schema {
    @type("string") sessionId: string = "";
    @type("string") name: string = "";
    @type("number") totalScore: number = 0;
    @type("number") currentStreak: number = 0;
    @type("string") avatarId: string = "";
    @type("int8") playerNumber: number = 0;
    @type("int8") rank: number = 0;
}

// Snapshot of game settings at time game ended (for recap display)
export class GameSettingsSnapshot extends Schema {
    @type("string") gameMode: string = "";
    @type("string") baseMode: string = ""; // For Custom mode
    @type("int8") maxRounds: number = 5;
    @type("int8") playerCount: number = 0;
    @type("int16") timerSeconds: number = 60;
    @type("int16") flashDuration: number = 1000;
    @type("int8") hazardsPerRound: number = 1;
    @type("int32") teamMaxHp: number = 10000;
    @type("string") damageMultiplierScaling: string = 'normal';
    @type("boolean") hasHazards: boolean = false;
    @type(["string"]) selectedExpansions = new ArraySchema<string>();
    @type(["string"]) selectedMapTypes = new ArraySchema<string>();
}

export class GameState extends Schema {
    /**
     * Get score multiplier for a team based on team size ratio
     * Teams with fewer members get a bonus to compensate
     */
    getTeamScoreMultiplier(teamId: string): number {
        const team = this.teams.find(t => t.id === teamId);
        if (!team) return 1;
        
        // Get the largest team size
        const maxMembers = Math.max(...this.teams.map(t => t.memberSessionIds.length), 1);
        const teamMembers = team.memberSessionIds.length;
        
        if (teamMembers === 0) return 1;
        
        // Multiplier = largest team size / this team size
        // E.g., if largest team has 4 and this team has 2, multiplier = 2
        return maxMembers / teamMembers;
    }

    /**
     * Apply damage to teams based on Fight mode scoring
     * Returns true if the fight has ended (only 1 team remaining)
     */
    applyFightModeDamage(teamMeanScores: Map<string, number>, damageMultiplier: number): boolean {
        const aliveTeams = this.teams.filter(t => !t.isEliminated && t.memberSessionIds.length > 0);
        
        if (aliveTeams.length <= 1) {
            return true; // Game already ended
        }
        
        // Find the best mean score
        const meanScores = Array.from(teamMeanScores.entries())
            .filter(([teamId]) => aliveTeams.some(t => t.id === teamId));
        
        if (meanScores.length === 0) return false;
        
        const maxMeanScore = Math.max(...meanScores.map(([, score]) => score));

        // for dead teams, set lastRoundDamage to 0
        for (const team of Array.from(this.teams)) {
            if (team.isEliminated) {
                team.lastRoundDamage = 0;
            }
        }
        
        // Calculate and apply damage to each team
        for (const team of aliveTeams) {
            const teamScore = teamMeanScores.get(team.id) || 0;
            const scoreDiff = maxMeanScore - teamScore;
            
            // Base damage is the score difference, scaled by the multiplier
            // Minimum damage of 100 per round for teams that didn't get the best score
            const baseDamage = scoreDiff > 0 ? Math.max(scoreDiff, 100) : 0;
            const damage = Math.round(baseDamage * damageMultiplier);
            
            team.lastRoundDamage = damage;
            team.currentHp = Math.max(0, team.currentHp - damage);
            
            if (team.currentHp <= 0) {
                team.isEliminated = true;

                for (const sessionId of Array.from(team.memberSessionIds)) {
                    const player = this.players.get(sessionId);
                    if (player) {
                        player.isEliminated = true;
                    }
                }
            }
        }
        
        // Check if fight has ended
        const remainingAlive = this.teams.filter(t => !t.isEliminated && t.memberSessionIds.length > 0);
        return remainingAlive.length <= 1;
    }

    /**
     * Create a new team with auto-generated name and color
     */
    createTeam(name?: string): Team | null {
        if (this.teams.length >= MAX_TEAMS) {
            return null;
        }
        
        const teamNumber = this.teams.length + 1;
        const teamId = `team_${teamNumber}_${Date.now()}`;
        const color = TEAM_COLORS_CONFIG[Math.floor(Math.random() * TEAM_COLORS_CONFIG.length)].id;
        const teamName = name || generateRandomTeamName();
        
        const team = new Team(teamId, teamName, color, teamNumber);
        
        // Initialize Fight mode HP if applicable
        if (this.gameSettings.gameMode === GameMode.FIGHT) {
            team.maxHp = this.gameSettings.teamMaxHp;
            team.currentHp = this.gameSettings.teamMaxHp;
        }
        
        this.teams.push(team);
        this.updateTeamModeEnabled();
        
        return team;
    }

    /**
     * Remove a team by ID
     */
    removeTeam(teamId: string): boolean {
        const index = this.teams.findIndex(t => t.id === teamId);
        if (index === -1) return false;
        
        const team = this.teams[index];
        
        // Clear all member assignments
        for (const sessionId of Array.from(team.memberSessionIds)) {
            const player = this.players.get(sessionId);
            if (player) {
                player.teamId = '';
            }
        }
        
        this.teams.splice(index, 1);
        
        // Renumber remaining teams (keep their colors)
        this.teams.forEach((t, i) => {
            t.teamNumber = i + 1;
        });
        
        this.updateTeamModeEnabled();
        return true;
    }

    /**
     * Clear all teams and reset player assignments
     */
    clearAllTeams(): void {
        // Clear player team assignments and coop roles
        for (const player of Array.from(this.players.values())) {
            player.teamId = '';
            player.coopRole = CoopRole.NONE;
        }
        
        // Clear teams
        this.teams.clear();
        this.updateTeamModeEnabled();
    }

    /**
     * Add a player to a team
     */
    addPlayerToTeam(sessionId: string, teamId: string): boolean {
        const team = this.teams.find(t => t.id === teamId);
        const player = this.players.get(sessionId);
        
        if (!team || !player) return false;
        
        // Remove from current team first
        this.removePlayerFromCurrentTeam(sessionId);
        
        // Add to new team
        team.addMember(sessionId);
        player.teamId = teamId;
        
        return true;
    }

    /**
     * Remove a player from their current team
     */
    removePlayerFromCurrentTeam(sessionId: string): boolean {
        const player = this.players.get(sessionId);
        if (!player || !player.teamId) return false;
        
        const currentTeam = this.teams.find(t => t.id === player.teamId);
        if (currentTeam) {
            currentTeam.removeMember(sessionId);
        }
        
        player.teamId = '';
        return true;
    }

    /**
     * Update the teamModeEnabled flag based on team count
     */
    private updateTeamModeEnabled(): void {
        this.teamModeEnabled = this.teams.length >= 2;
    }
    @type({ map: Player }) players = new MapSchema<Player>();
    @type([Team]) teams = new ArraySchema<Team>(); // Teams array (empty = free-for-all, 1 team = free-for-all, 2-4 teams = team mode)
    @type([TeamRanking]) teamRankings = new ArraySchema<TeamRanking>(); // Final team rankings
    @type("boolean") teamModeEnabled: boolean = false; // Quick flag to check if team mode is active
    @type("float32") currentDamageMultiplier: number = 1; // Current damage multiplier for Fight mode (increases each round)

    @type("int8") currentRound: number = 0;
    @type("int8") gameStep: GameStep = GameStep.LOBBY;
    @type("string") gameMasterId: string = "";
    @type("string") gameMode: GameMode = GameMode.TIMED;
    @type("boolean") showSeedPanel: boolean = false; // Synced seed panel visibility
    @type("string") seedPanelInput: string = ""; // Synced seed input for all players to see
    @type("number") roundStartTime: number = 0;

    @type(GameModeSettingsSchema) gameSettings = new GameModeSettingsSchema();

    @view() @type({ map: Location }) locations = new MapSchema<Location>();
    @type(["string"]) selectedExpansions = new ArraySchema<string>();
    @type(["string"]) selectedMapTypes = new ArraySchema<string>();
    @type([Photosphere]) gamePhotospheres = new ArraySchema<Photosphere>();
    @type(Photosphere) currentPhotosphere = new Photosphere();
    @type(Photosphere) nextPhotosphere = new Photosphere(); // For preloading the next round's photosphere
    @type("boolean") allPlayersLoadedPhotosphere: boolean = false; // True when all players have loaded the current photosphere
    @type(["string"]) currentHazards = new ArraySchema<string>(); // Current hazards for Hazard mode
    @type([RoundResult]) roundHistory = new ArraySchema<RoundResult>();
    @type([FinalRanking]) finalRankings = new ArraySchema<FinalRanking>();
    @type(GameSettingsSnapshot) gameSettingsSnapshot = new GameSettingsSnapshot(); // Snapshot of settings at game end
    @type("number") resultDisplayStartTime: number = 0;

    get maxRounds(): number {
        return this.gameSettings?.maxRounds ?? 5;
    }

    get timerSeconds(): number | null {
        return this.gameSettings?.actualTimerSeconds ?? null;
    }



    get hasSpecialRules(): boolean {
        return this.gameSettings?.hasSpecialRules ?? false;
    }

    get status(): GameModeStatus {
        return this.gameSettings?.status ?? GameModeStatus.AVAILABLE;
    }

    get minPlayers(): number {
        return this.gameSettings?.minPlayers ?? 1;
    }

    get maxPlayers(): number {
        return this.gameSettings?.maxPlayers ?? 12;
    }

    get fightModeSettings(): FightModeSettings | null {
           if (!this.gameSettings || this.gameSettings.gameMode !== GameMode.FIGHT) return null;
           return {
              gameMode: GameMode.FIGHT,
              maxRounds: -1,
              timerSeconds: this.gameSettings.timerSeconds > 0 ? this.gameSettings.timerSeconds : 60,
              hasSpecialRules: this.gameSettings.hasSpecialRules,
              status: this.gameSettings.status,
              minPlayers: this.gameSettings.minPlayers,
              maxPlayers: this.gameSettings.maxPlayers,
              teamMaxHp: this.gameSettings.teamMaxHp,
              numberOfTeams: this.gameSettings.numberOfTeams,
              damageMultiplierScaling: this.gameSettings.damageMultiplierScaling
           };
    }

    get hazardModeSettings(): HazardModeSettings | null {
           if (!this.gameSettings || this.gameSettings.gameMode !== GameMode.HAZARD) return null;
           return {
              gameMode: GameMode.HAZARD,
              maxRounds: this.gameSettings.maxRounds,
              timerSeconds: this.gameSettings.timerSeconds > 0 ? this.gameSettings.timerSeconds : 60,
              hasSpecialRules: this.gameSettings.hasSpecialRules,
              status: this.gameSettings.status,
              minPlayers: this.gameSettings.minPlayers,
              maxPlayers: this.gameSettings.maxPlayers,
              hazardsPerRound: this.gameSettings.hazardsPerRound
           };
    }

    get flashModeSettings(): FlashModeSettings | null {
           if (!this.gameSettings || this.gameSettings.gameMode !== GameMode.FLASH) return null;
           return {
              gameMode: GameMode.FLASH,
              maxRounds: this.gameSettings.maxRounds,
              timerSeconds: null,
              hasSpecialRules: this.gameSettings.hasSpecialRules,
              status: this.gameSettings.status,
              minPlayers: this.gameSettings.minPlayers,
              maxPlayers: this.gameSettings.maxPlayers,
              flashDuration: this.gameSettings.flashDuration
           };
    }

    get dailyModeSettings(): DailyModeSettings | null {
           if (!this.gameSettings || this.gameSettings.gameMode !== GameMode.DAILY) return null;
           return {
              gameMode: GameMode.DAILY,
              maxRounds: this.gameSettings.maxRounds,
              timerSeconds: this.gameSettings.timerSeconds > 0 ? this.gameSettings.timerSeconds : 0,
              hasSpecialRules: this.gameSettings.hasSpecialRules,
              status: this.gameSettings.status,
              minPlayers: this.gameSettings.minPlayers,
              maxPlayers: this.gameSettings.maxPlayers,
              dailySeed: this.gameSettings.dailySeed
           };
    }

    get coopModeSettings(): CoopModeSettings | null {
           if (!this.gameSettings || this.gameSettings.gameMode !== GameMode.COOP) return null;
           return {
              gameMode: GameMode.COOP,
              maxRounds: this.gameSettings.maxRounds,
              timerSeconds: this.gameSettings.timerSeconds > 0 ? this.gameSettings.timerSeconds : 60,
              hasSpecialRules: this.gameSettings.hasSpecialRules,
              status: this.gameSettings.status,
              minPlayers: this.gameSettings.minPlayers,
              maxPlayers: this.gameSettings.maxPlayers,
              numberOfTeams: this.gameSettings.numberOfTeams
           };
    }

    get customModeSettings(): CustomModeSettings | null {
           if (!this.gameSettings || this.gameSettings.gameMode !== GameMode.CUSTOM) return null;
           return {
              gameMode: GameMode.CUSTOM,
              baseMode: this.gameSettings.baseMode as CustomBaseMode,
              maxRounds: this.gameSettings.maxRounds,
              timerSeconds: this.gameSettings.timerSeconds > 0 ? this.gameSettings.timerSeconds : null,
              hasSpecialRules: this.gameSettings.hasSpecialRules,
              status: this.gameSettings.status,
              minPlayers: this.gameSettings.minPlayers,
              maxPlayers: this.gameSettings.maxPlayers,
              // Flash mode
              hasFlashMode: this.gameSettings.hasFlashMode,
              flashDuration: this.gameSettings.flashDuration,
              // Hazard mode
              hasHazards: this.gameSettings.hasHazards,
              hazardsPerRound: this.gameSettings.hazardsPerRound,
              enabledHazards: this.gameSettings.enabledHazards?.length > 0 ? Array.from(this.gameSettings.enabledHazards) : undefined,
              // Fight/Coop mode
              teamMaxHp: this.gameSettings.teamMaxHp,
              numberOfTeams: this.gameSettings.numberOfTeams,
              damageMultiplierScaling: this.gameSettings.damageMultiplierScaling,
              // Compass
              compassDisabled: this.gameSettings.compassDisabled
           };
    }

    getSelectedExpansionsAsEnum(): Expansion[] {
        return this.selectedExpansions.filter(exp =>
            Object.values(Expansion).includes(exp as Expansion)
        ) as Expansion[];
    }

    setSelectedExpansions(expansions: Expansion[]): void {
        this.selectedExpansions.clear();
        expansions.forEach(exp => this.selectedExpansions.push(exp));
    }

    getSelectedMapTypesAsEnum(): MapType[] {
        return this.selectedMapTypes.filter(type =>
            Object.values(MapType).includes(type as MapType)
        ) as MapType[];
    }

    setSelectedMapTypes(mapTypes: MapType[]): void {
        this.selectedMapTypes.clear();
        mapTypes.forEach(type => this.selectedMapTypes.push(type));
    }

    getGameModeConfig(): GameModeAdvancedSettings {
        return this.gameSettings.toAdvancedSettings();
    }

    initializeFromGameMode(gameMode: GameMode, customSettings?: Partial<GameModeAdvancedSettings>): void {
        const defaultConfig = getDefaultAdvancedSettings(gameMode);
        
        // Merge default config with custom settings
        const mergedSettings = {
            ...defaultConfig,
            ...customSettings
        } as GameModeAdvancedSettings;

        // Update the synchronized schema object
        this.gameSettings.fromAdvancedSettings(mergedSettings);
        this.gameMode = gameMode;
    }

    updateGameSettings(newSettings: Partial<GameModeAdvancedSettings>): void {
        const currentSettings = this.gameSettings.toAdvancedSettings();
        const mergedSettings = {
            ...currentSettings,
            ...newSettings
        } as GameModeAdvancedSettings;
        
        // Update the synchronized schema object
        this.gameSettings.fromAdvancedSettings(mergedSettings);
    }

    // Team management helper methods
    
    /**
     * Check if team mode is currently active (2+ teams)
     */
    get isTeamMode(): boolean {
        return this.teamModeEnabled && this.teams.length >= 2;
    }

    /**
     * Get the team a player belongs to
     */
    getPlayerTeam(sessionId: string): Team | null {
        for (const team of Array.from(this.teams)) {
            if (team.hasMember(sessionId)) {
                return team;
            }
        }
        return null;
    }

    /**
     * Get count of alive teams (for Fight mode)
     */
    get aliveTeamsCount(): number {
        return this.teams.filter(t => !t.isEliminated && t.memberCount > 0).length;
    }

    /**
     * Get the winning team (for Fight mode - only if exactly one team alive)
     */
    get winningTeam(): Team | null {
        const aliveTeams = this.teams.filter(t => !t.isEliminated && t.memberCount > 0);
        return aliveTeams.length === 1 ? aliveTeams[0] : null;
    }

    // ==================== CUSTOM MODE HELPER METHODS ====================
    
    /**
     * Get the effective base mode (for Custom mode, returns the baseMode; otherwise returns gameMode)
     * This is used to determine which game rules to apply
     */
    get effectiveBaseMode(): GameMode {
        if (!this.gameSettings) return GameMode.CLASSIC;
        if (this.gameSettings.gameMode === GameMode.CUSTOM) {
            return this.gameSettings.baseMode as GameMode;
        }
        return this.gameSettings.gameMode;
    }

    /**
     * Check if the game should behave like Endless mode
     */
    get isEffectivelyEndless(): boolean {
        if (!this.gameSettings) return false;
        return this.gameSettings.gameMode === GameMode.ENDLESS || 
               (this.gameSettings.gameMode === GameMode.CUSTOM && this.gameSettings.baseMode === GameMode.ENDLESS);
    }

    /**
     * Check if the game should behave like Fight mode
     */
    get isEffectivelyFight(): boolean {
        if (!this.gameSettings) return false;
        return this.gameSettings.gameMode === GameMode.FIGHT || 
               (this.gameSettings.gameMode === GameMode.CUSTOM && this.gameSettings.baseMode === GameMode.FIGHT);
    }

    /**
     * Check if the game should behave like Coop mode
     */
    get isEffectivelyCoop(): boolean {
        if (!this.gameSettings) return false;
        return this.gameSettings.gameMode === GameMode.COOP || 
               (this.gameSettings.gameMode === GameMode.CUSTOM && this.gameSettings.baseMode === GameMode.COOP);
    }

    /**
     * Check if flash mode is active (either Flash mode or Custom with hasFlashMode)
     */
    get hasFlashModeActive(): boolean {
        if (!this.gameSettings) return false;
        return this.gameSettings.gameMode === GameMode.FLASH || 
               (this.gameSettings.gameMode === GameMode.CUSTOM && this.gameSettings.hasFlashMode);
    }

    /**
     * Check if hazards are active (either Hazard mode or Custom with hasHazards)
     */
    get hasHazardsActive(): boolean {
        if (!this.gameSettings) return false;
        return this.gameSettings.gameMode === GameMode.HAZARD || 
               (this.gameSettings.gameMode === GameMode.CUSTOM && this.gameSettings.hasHazards);
    }

    /**
     * Check if teams are required for this game mode
     */
    get requiresTeams(): boolean {
        return this.isEffectivelyFight || this.isEffectivelyCoop;
    }
}
